import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { parseConvertedChildrenFromApiResponse } from './document-conversion-lock.util';
import { DocumentService } from './document.service';
import { normalizeStatutKeyForCompare } from './document-type-statut-workflow.util';
import { formatErpDocumentCode } from './pv-ecart-linked-documents.util';
import {
  ERP_WORKFLOW_STATUT,
  isBonTransfertDocumentFromPvEcart,
  resolveTransferWorkflowRootId,
} from './stock-transfer-workflow.util';
import { STOCK_TRANSFER_WORKFLOW_TYPE_IDS } from './stock-transfer-workflow.constants';

function pickLatestChildByType(children: any[], typeId: number): any | null {
  const list = (children || []).filter((c) => Number(c?.type_document_id) === typeId);
  if (!list.length) {
    return null;
  }
  return list.sort((a, b) => Number(b?.id) - Number(a?.id))[0];
}

/** PV d'écart actif (hors brouillon / annulé) déjà rattaché au workflow. */
export function workflowHasActivePvEcartChild(children: any[]): boolean {
  if (!Array.isArray(children) || !children.length) {
    return false;
  }
  return children.some((c) => {
    if (Number(c?.type_document_id) !== STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART) {
      return false;
    }
    const sid = Number(c?.statut_id ?? c?.statut?.id);
    if (sid === ERP_WORKFLOW_STATUT.BROUILLON || sid === 1) {
      return false;
    }
    if (sid === 6) {
      return false;
    }
    return true;
  });
}

export function resolveBonTransfertIdForPvEcartGeneration(doc: Record<string, unknown>): number | null {
  const typeId = Number(doc['type_document_id'] ?? (doc['typeDocument'] as { id?: number })?.id);
  const docId = Number(doc['id']);
  if (typeId === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT && Number.isFinite(docId) && docId > 0) {
    return docId;
  }
  const rootId = resolveTransferWorkflowRootId(doc);
  if (rootId != null && rootId > 0) {
    return rootId;
  }
  return null;
}

export function isBonTransfertStatutEligibleForPvEcart(
  statutId: number | null | undefined,
  statutTitre?: string | null
): boolean {
  const sid = statutId != null ? Number(statutId) : NaN;
  if (sid === ERP_WORKFLOW_STATUT.TRANSFERE || sid === 17) {
    return true;
  }
  const key = normalizeStatutKeyForCompare(String(statutTitre ?? ''));
  return key === 'TRANSFERE';
}

export function isBonReceptionStatutEligibleForPvEcart(
  statutId: number | null | undefined,
  statutTitre?: string | null
): boolean {
  const sid = statutId != null ? Number(statutId) : NaN;
  const key = normalizeStatutKeyForCompare(String(statutTitre ?? ''));
  if (key === 'BROUILLON' || key === 'ANNULE' || key === 'CLOTURE') {
    return false;
  }
  const eligibleIds = new Set([
    ERP_WORKFLOW_STATUT.EN_CONTROLE,
    ERP_WORKFLOW_STATUT.RECEPTIONNE,
    ERP_WORKFLOW_STATUT.RECEPTION_PARTIELLE,
    ERP_WORKFLOW_STATUT.AVEC_ECART,
    22,
    23,
    24,
    25,
  ]);
  if (Number.isFinite(sid) && eligibleIds.has(sid)) {
    return true;
  }
  return (
    key === 'EN CONTROLE' ||
    key === 'RECEPTIONNE' ||
    key === 'RECEPTION PARTIELLE' ||
    key === 'AVEC ECART' ||
    key === 'RECU'
  );
}

export function extractPvEcartIdFromGenerateApiResponse(res: unknown): number | null {
  const r = res as Record<string, unknown> | null | undefined;
  const data = (r?.['data'] as Record<string, unknown>) ?? r;
  const doc = (data?.['document'] as Record<string, unknown>) ?? data;
  const raw = doc?.['id'] ?? data?.['id'] ?? r?.['id'];
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function workflowHasActivePvEcart$(
  documentService: DocumentService,
  doc: Record<string, unknown>
): Observable<boolean> {
  const rootId = resolveTransferWorkflowRootId(doc) ?? resolveBonTransfertIdForPvEcartGeneration(doc);
  if (rootId == null || rootId <= 0) {
    return of(false);
  }
  return documentService.getDocumentsByOriginalId(rootId).pipe(
    map((res) => workflowHasActivePvEcartChild(parseConvertedChildrenFromApiResponse(res))),
    catchError(() => of(false))
  );
}

/** POST generate-pv-ecart puis id du PV créé. */
export function createPvEcartFromBonTransfert$(
  documentService: DocumentService,
  bonTransfertId: number
): Observable<{ pvId: number; message?: string }> {
  return documentService.generatePvEcartFromBonTransfert(bonTransfertId).pipe(
    map((res) => {
      const pvId = extractPvEcartIdFromGenerateApiResponse(res);
      if (pvId == null) {
        throw new Error('PV d\'écart créé sans identifiant retourné par l\'API.');
      }
      const r = res as { message?: string };
      return { pvId, message: r?.message };
    })
  );
}

/** Création PV depuis un bon de réception (via bon de transfert racine du workflow). */
export function createPvEcartFromBonReception$(
  documentService: DocumentService,
  bonReceptionDoc: Record<string, unknown>
): Observable<{ pvId: number; message?: string }> {
  const btId = resolveBonTransfertIdForPvEcartGeneration(bonReceptionDoc);
  if (btId == null) {
    return throwError(
      () => new Error('Bon de transfert racine introuvable pour générer le PV d\'écart.')
    );
  }
  return createPvEcartFromBonTransfert$(documentService, btId);
}

/**
 * Référence mouvement stock : pour un BT issu d'un PV validé, utiliser le code du PV (traçabilité).
 */
export function resolvePvEcartStockReference$(
  documentService: DocumentService,
  doc: Record<string, unknown>,
  fallbackRef: string
): Observable<string> {
  if (!isBonTransfertDocumentFromPvEcart(doc)) {
    return of(fallbackRef);
  }
  const rootId = resolveTransferWorkflowRootId(doc);
  if (rootId == null || rootId <= 0) {
    return of(fallbackRef);
  }
  return documentService.getDocumentsByOriginalId(rootId).pipe(
    map((res) => {
      const children = parseConvertedChildrenFromApiResponse(res);
      const pv = pickLatestChildByType(children, STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART);
      if (!pv) {
        return fallbackRef;
      }
      const ref = formatErpDocumentCode(pv);
      return ref !== '—' ? ref : fallbackRef;
    }),
    catchError(() => of(fallbackRef))
  );
}
